import { LinearProgress } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useHealthRecord } from "../../../../store";
import { useAppContext } from "../../../hooks/useAppContext";
import { useConfig } from "../../../hooks/useConfig";
import { useFetchHealthRecord } from "../../../hooks/useFetchHealthRecord";
import { useI18n } from "../../../hooks/useI18n";
import { useApis } from "../../../providers/Dependencies";

export function IframeDiagnostic() {
  const { id } = useParams() as { id: string };
  const { organizationId, teamId } = useAppContext();
  const { locale } = useI18n();
  const { sadvIframeUrl } = useConfig();
  const apis = useApis();
  const healthRecord = useHealthRecord(id);
  const [evcToken, setEvcToken] = useState<string | undefined>(undefined);
  const [iframeHeight, setIframeHeight] = useState<number>(500);
  const [isEvcLoading, setIsEvcLoading] = useState<boolean>(true);
  const [isIframeDiagnosticLoading, setIsIframeDiagnosticLoading] = useState<boolean>(true);

  useFetchHealthRecord(id);

  const mentorLocale = useMemo(() => {
    switch (locale) {
      case "fr":
        return "fr";
      case "en-gb":
        return "en";
      default:
        return "en";
    }
  }, [locale]);

  const iframeUrl = useMemo(() => {
    if (!sadvIframeUrl) return "";

    const url = new URL(sadvIframeUrl);
    url.pathname = url.hostname.includes("sadv-iframe") ? "/" : "/iframe";
    url.search = "";
    url.hash = "";
    return url.toString();
  }, [sadvIframeUrl]);

  useEffect(() => {
    if (!iframeUrl) return;

    const expectedOrigin = new URL(iframeUrl).origin;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) {
        return;
      }

      switch (event.data?.type) {
        case "documentOpen":
          if (event.data?.data?.url) {
            window.open(event.data.data.url, "_blank", "noopener,noreferrer");
          }
          break;
        case "heightChange":
          if (event.data?.data?.height && typeof event.data.data.height === "number") {
            setIframeHeight(Math.max(event.data.data.height + 20, 500));
          }
          break;
        case "diagnosticLoadingCompleted":
          setIsIframeDiagnosticLoading(false);
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [iframeUrl]);

  useEffect(() => {
    const fetchEvcToken = async () => {
      try {
        setIsEvcLoading(true);
        setIsIframeDiagnosticLoading(true);

        const evcResponse =
          organizationId && teamId
            ? await apis.team.healthRecordApi.getEvcToken(organizationId, teamId, id)
            : await apis.pro.healthRecordApi.getEvcToken(id);

        setEvcToken(evcResponse.jwt);
      } catch (error) {
        console.error("Error fetching EVC token:", error);
        setEvcToken(undefined);
        setIsIframeDiagnosticLoading(false);
      } finally {
        setIsEvcLoading(false);
      }
    };

    fetchEvcToken();
  }, [apis, id, organizationId, teamId]);

  const isLoading = isEvcLoading || isIframeDiagnosticLoading;

  if (!healthRecord) {
    return <LinearProgress variant="query" />;
  }

  return (
    <div style={{ position: "relative", minHeight: 200 }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 10,
          }}
        >
          <LinearProgress variant="query" />
        </div>
      )}
      <iframe
        src={
          evcToken && iframeUrl
            ? `${iframeUrl}?frozen=true&step=recommandations&lang=${mentorLocale}&prevent-iframe-scroll=true&evc=${encodeURIComponent(evcToken)}&pro=true`
            : ""
        }
        width="100%"
        height={iframeHeight}
        title="SADV iframe"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        style={{ border: 0, opacity: isLoading ? 0 : 1, transition: "opacity 0.3s" }}
      />
    </div>
  );
}
